import React, { FunctionComponent, useState } from 'react';
import { Checkbox, Form, Input } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppStore, userActions } from '@akademiasaas/shared';
import { useQuery } from '~/hooks/useQuery';
import { useLoading } from '~/hooks/useLoading';
import { useAppDispatch } from '~/initializeStore';
import { ErrorSummary, formLayout, useFormErrorSummary } from '~/components/ui';
import { getUserIp } from '~/pages/Auth/helpers/checkUserIp';
import AuthCard, { AuthHeading, AUTH_TITLE_ID } from '../../layout/AuthCard';
import { LEGAL_LINKS } from '../../layout/AuthLayout';
import AuthErrorCallout, { isCalloutKind } from '../../form/AuthErrorCallout';
import PasswordInput from '../../form/PasswordInput';
import SubmitButton from '../../form/SubmitButton';
import { authErrorKind } from '../../form/authErrors';
import { acceptedRule, emailRules, newPasswordRules, requiredRule } from '../../form/authRules';
import PhoneField from './PhoneField';
import { phoneWithPrefix } from './phonePrefix';
import styles from '../../form/authForm.module.scss';

interface RegisterFormModel {
  firstName: string;
  lastName: string;
  email: string;
  phoneCountry: string;
  phoneNumber?: string;
  password: string;
  termsAndPrivacyPolicy: boolean;
  uppdProcessingAgreement: boolean;
}

const FORM_NAME = 'register';
const UPPD_URL = 'https://docuwatcher.com/uppd/';
const external = { target: '_blank', rel: 'noopener noreferrer' } as const;

export const Register: FunctionComponent = () => {
  const { t, i18n } = useTranslation(['auth']);
  const dispatch = useAppDispatch();
  const query = useQuery();
  const [form] = Form.useForm<RegisterFormModel>();
  const [busy, startLoading, stopLoading] = useLoading();
  const { registerError } = useSelector((store: AppStore) => store.user);
  // Błąd z poprzedniej wizyty zostaje w sklepie — pokazujemy tylko wynik wysyłki z tego ekranu.
  const [submitted, setSubmitted] = useState<{ email: string; password: string } | null>(null);
  const email = (Form.useWatch('email', form) ?? '').trim();
  const password = Form.useWatch('password', form) ?? '';
  const summary = useFormErrorSummary<RegisterFormModel>(FORM_NAME);

  const errorKind = submitted && !busy ? authErrorKind(registerError) : null;
  // Błędy z serwera stoją przy polu tylko, dopóki wartość jest ta wysłana; po poprawie wracają reguły.
  const emailTaken = errorKind === 'emailInUse' && email === submitted?.email;
  const emailRejected = errorKind === 'invalidEmail' && email === submitted?.email;
  const weakPassword = errorKind === 'weakPassword' && password === submitted?.password;
  const fieldKinds = ['emailInUse', 'invalidEmail', 'weakPassword'];
  const calloutKind =
    errorKind && !fieldKinds.includes(errorKind)
      ? isCalloutKind(errorKind)
        ? errorKind
        : 'unknown'
      : null;

  const onFinish = async (values: RegisterFormModel) => {
    if (busy) return;
    const trimmedEmail = values.email.trim();
    startLoading();
    setSubmitted({ email: trimmedEmail, password: values.password });
    const ip = await getUserIp();
    await dispatch(
      userActions.signUpUser(
        {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          email: trimmedEmail,
          password: values.password,
          termsAndPrivacyPolicy: values.termsAndPrivacyPolicy,
          uppdProcessingAgreement: values.uppdProcessingAgreement,
          ip,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          lang: i18n.language,
          phoneNumber: phoneWithPrefix(values.phoneCountry, values.phoneNumber),
        },
        undefined,
        stopLoading
      )
    );
  };

  return (
    <AuthCard
      wide
      footer={
        <>
          {t('auth:screens.register.hasAccount')}{' '}
          <Link to="/auth/login">{t('auth:screens.register.login')}</Link>
        </>
      }
    >
      <AuthHeading
        title={t('auth:screens.register.title')}
        lead={t('auth:screens.register.lead')}
      />
      {calloutKind && <AuthErrorCallout kind={calloutKind} context="register" />}
      {summary.items.length > 0 && (
        <div className={styles.callout}>
          <ErrorSummary
            ref={summary.summaryRef}
            items={summary.items}
            title={t('auth:screens.validation.summary', { count: summary.items.length })}
          />
        </div>
      )}
      <Form<RegisterFormModel>
        form={form}
        name={FORM_NAME}
        layout="vertical"
        requiredMark={false}
        validateTrigger="onBlur"
        className={`${formLayout.form} ${styles.form}`}
        aria-labelledby={AUTH_TITLE_ID}
        initialValues={{
          email: query.get('email') ?? '',
          phoneCountry: 'PL',
          termsAndPrivacyPolicy: false,
          uppdProcessingAgreement: false,
        }}
        onFinish={onFinish}
        onFinishFailed={summary.onFinishFailed}
        onFieldsChange={summary.onFieldsChange}
      >
        <div className={styles.row}>
          <Form.Item
            name="firstName"
            label={t('auth:screens.field.firstName')}
            rules={requiredRule(t('auth:screens.validation.firstNameRequired'))}
          >
            <Input autoComplete="given-name" aria-required="true" autoFocus readOnly={busy} />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('auth:screens.field.lastName')}
            rules={requiredRule(t('auth:screens.validation.lastNameRequired'))}
          >
            <Input autoComplete="family-name" aria-required="true" readOnly={busy} />
          </Form.Item>
        </div>
        <Form.Item
          name="email"
          label={t('auth:screens.field.email')}
          rules={emailRules(t)}
          validateStatus={emailTaken || emailRejected ? 'error' : undefined}
          help={
            emailTaken ? (
              <Trans
                t={t}
                i18nKey="auth:screens.errors.emailInUse"
                components={{ go: <Link to="/auth/login" /> }}
              />
            ) : emailRejected ? (
              t('auth:screens.validation.emailInvalid')
            ) : undefined
          }
        >
          <Input
            type="email"
            autoComplete="email"
            aria-required="true"
            // Błąd z serwera (help przy polu) — antd ustawia aria-invalid tylko dla walidacji reguł.
            aria-invalid={emailTaken || emailRejected || undefined}
            readOnly={busy}
          />
        </Form.Item>
        <PhoneField formName={FORM_NAME} disabled={busy} />
        <Form.Item
          name="password"
          label={t('auth:screens.field.password')}
          rules={newPasswordRules(t)}
          extra={t('auth:screens.field.passwordHint')}
          validateStatus={weakPassword ? 'error' : undefined}
          help={weakPassword ? t('auth:screens.errors.weakPassword') : undefined}
        >
          <PasswordInput
            autoComplete="new-password"
            aria-required="true"
            aria-invalid={weakPassword || undefined}
            readOnly={busy}
          />
        </Form.Item>
        <div className={styles.consents}>
          <Form.Item
            name="termsAndPrivacyPolicy"
            valuePropName="checked"
            rules={acceptedRule(t('auth:screens.validation.termsRequired'))}
          >
            <Checkbox aria-required="true" disabled={busy}>
              <Trans
                t={t}
                i18nKey="auth:registerConsentTermsPrivacy"
                components={{
                  termsLink: <a href={LEGAL_LINKS.terms} {...external} />,
                  privacyLink: <a href={LEGAL_LINKS.privacy} {...external} />,
                }}
              />
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="uppdProcessingAgreement"
            valuePropName="checked"
            rules={acceptedRule(t('auth:screens.validation.uppdRequired'))}
          >
            <Checkbox aria-required="true" disabled={busy}>
              <Trans
                t={t}
                i18nKey="auth:registerConsentUppd"
                components={{ uppdLink: <a href={UPPD_URL} {...external} /> }}
              />
            </Checkbox>
          </Form.Item>
        </div>
        <SubmitButton
          label={t('auth:screens.register.submit')}
          busyLabel={t('auth:screens.register.busy')}
          busy={busy}
        />
      </Form>
    </AuthCard>
  );
};
